import './FinancialFilters.scss'
import React from "react";
import {observer} from "mobx-react-lite";
import {useSitStores} from "~/stores";
import type {ValueType} from "../../ApiTypes";
import {DOIs} from "../../ApiTypes";
import {stageGateLabel} from "~/sit/sitLabels.ts";
import type {FinancialFilterValues} from "../../stores/FinancialDashboardStore";
import {SitFilterBar, type SitFilterDef, SitOptionList} from "../../components/filters/SitFilterBar";
import {sitFilterStatus, useSitFilterDraft, withExcluded} from "../../components/filters/sitFilterDraft";
import i18n from "../../../i18n/i18n";

const fin = (k: string) => i18n.t(`fin.${k}`, {ns: 'sit'});

/** Filters of the Financial, Pipeline and Indicators views; changes load together with Apply. */
export const FinancialFilters: React.FC = observer(() => {
    const {ccStore, fbStore} = useSitStores();

    const thisYear = new Date().getFullYear();
    const noFilters: FinancialFilterValues = {
        year: thisYear, activeValueType: 'rolling', probabilityAdjusted: false, activeFilteredCompanies: [], scope: '',
        dois: [], activeLeverGroups: [], activeCategories: [], approved: '', excluded: [],
    };
    const applied: FinancialFilterValues = {
        year: fbStore.year, activeValueType: fbStore.activeValueType, probabilityAdjusted: fbStore.probabilityAdjusted,
        activeFilteredCompanies: fbStore.activeFilteredCompanies, scope: fbStore.scope, dois: fbStore.dois,
        activeLeverGroups: fbStore.activeLeverGroups, activeCategories: fbStore.activeCategories, approved: fbStore.approved,
        excluded: fbStore.excluded,
    };
    const [draft, change, dirty] = useSitFilterDraft(applied);
    const excl = (key: string) => draft.excluded.includes(key);
    const excluded = (key: string, excluding: boolean) => withExcluded(draft.excluded, key, excluding);

    const companies = ccStore.companyRM.result ?? [];
    const clusters = ccStore.clusterRM.result ?? [];
    const leverGroups = ccStore.leverGroupRM.result ?? [];
    const categories = ccStore.allCategories ?? [];
    const companyIds = draft.activeFilteredCompanies.map(c => c.id);
    // A cluster is a shortcut for its companies: selected when all of them are.
    const clusterCompanies = (id: number) => companies.filter(c => c.cluster.id === id).map(c => c.id);
    const selectedClusters = clusters.filter(cl => {
        const ids = clusterCompanies(cl.id);
        return ids.length > 0 && ids.every(id => companyIds.includes(id));
    }).map(cl => cl.id);
    const years = Array.from({length: 7}, (_v, i) => thisYear - 5 + i);
    const valueTypes: {value: ValueType; label: string}[] = [
        {value: 'rolling', label: fin('rolling')}, {value: 'fiscal_year', label: fin('fiscalYear')},
    ];
    const yesNo = [{value: 'yes', label: i18n.t('filters.yes', {ns: 'sit'})}, {value: 'no', label: i18n.t('filters.no', {ns: 'sit'})}];
    const scopes = [
        {value: '', label: fin('all')}, {value: 'local', label: fin('local')}, {value: 'global', label: fin('global')},
    ];
    const approvals = [
        {value: '', label: fin('all')}, {value: 'approved', label: fin('approved')}, {value: 'pending', label: fin('pending')},
    ];

    const filters: SitFilterDef[] = [
        {
            id: 'year', label: fin('analysisYear'), primary: true, active: draft.year !== thisYear, status: String(draft.year),
            render: close => <SitOptionList options={years.map(y => ({value: y, label: String(y)}))} selected={[draft.year]}
                                            onChange={([year]) => { change({year}); close(); }}/>,
        },
        {
            id: 'savings', label: fin('savings'), active: draft.activeValueType !== 'rolling',
            status: valueTypes.find(v => v.value === draft.activeValueType)?.label ?? '',
            render: close => <SitOptionList options={valueTypes} selected={[draft.activeValueType]}
                                            onChange={([activeValueType]) => { change({activeValueType}); close(); }}/>,
        },
        {
            id: 'company', label: fin('company'), primary: true, active: companyIds.length > 0,
            status: sitFilterStatus(draft.activeFilteredCompanies.map(c => c.title), excl('companies'), companies.length),
            render: () => <SitOptionList multiple options={companies.map(c => ({value: c.id, label: c.title}))}
                                         selected={companyIds} excluding={excl('companies')}
                                         onChange={(ids, x) => change({
                                             activeFilteredCompanies: companies.filter(c => ids.includes(c.id)),
                                             excluded: excluded('companies', x),
                                         })}/>,
        },
        {
            id: 'stage', label: fin('stage'), primary: true, active: draft.dois.length > 0,
            status: sitFilterStatus(draft.dois.map(stageGateLabel), excl('dois'), DOIs.length),
            render: () => <SitOptionList multiple options={DOIs.map(d => ({value: d, label: stageGateLabel(d)}))}
                                         selected={draft.dois} excluding={excl('dois')}
                                         onChange={(dois, x) => change({dois, excluded: excluded('dois', x)})}/>,
        },
        {
            id: 'probability', label: fin('probabilityAdjusted'), active: draft.probabilityAdjusted,
            status: draft.probabilityAdjusted ? yesNo[0].label : yesNo[1].label,
            render: close => <SitOptionList options={yesNo} selected={[draft.probabilityAdjusted ? 'yes' : 'no']}
                                            onChange={([v]) => { change({probabilityAdjusted: v === 'yes'}); close(); }}/>,
        },
        {
            id: 'cluster', label: fin('cluster'), active: selectedClusters.length > 0,
            // A shortcut for its companies, so it shares the Company filter's Excluding mode.
            status: sitFilterStatus(clusters.filter(cl => selectedClusters.includes(cl.id)).map(cl => cl.title), excl('companies'), clusters.length),
            render: () => <SitOptionList multiple options={clusters.map(cl => ({value: cl.id, label: cl.title}))}
                                         selected={selectedClusters} excluding={excl('companies')}
                                         onChange={(ids, x) => {
                                             const added = ids.filter(id => !selectedClusters.includes(id));
                                             const removed = selectedClusters.filter(id => !ids.includes(id));
                                             const next = new Set(companyIds);
                                             added.forEach(id => clusterCompanies(id).forEach(c => next.add(c)));
                                             removed.forEach(id => clusterCompanies(id).forEach(c => next.delete(c)));
                                             change({
                                                 activeFilteredCompanies: companies.filter(c => next.has(c.id)),
                                                 excluded: excluded('companies', x && next.size > 0),
                                             });
                                         }}/>,
        },
        {
            id: 'scope', label: fin('scope'), active: draft.scope !== '',
            status: scopes.find(s => s.value === draft.scope)?.label ?? '',
            render: close => <SitOptionList options={scopes} selected={[draft.scope]}
                                            onChange={([v]) => { change({scope: v as FinancialFilterValues['scope']}); close(); }}/>,
        },
        {
            id: 'leverGroups', label: fin('leverGroups'), active: draft.activeLeverGroups.length > 0,
            status: sitFilterStatus(draft.activeLeverGroups.map(l => l.title), excl('leverGroups'), leverGroups.length),
            render: () => <SitOptionList multiple options={leverGroups.map(l => ({value: l.id, label: l.title}))}
                                         selected={draft.activeLeverGroups.map(l => l.id)} excluding={excl('leverGroups')}
                                         onChange={(ids, x) => change({
                                             activeLeverGroups: leverGroups.filter(l => ids.includes(l.id)),
                                             excluded: excluded('leverGroups', x),
                                         })}/>,
        },
        {
            id: 'category', label: fin('category'), active: draft.activeCategories.length > 0,
            status: sitFilterStatus(draft.activeCategories.map(c => c.label), excl('categories'), categories.length),
            render: () => <SitOptionList multiple options={categories.map(c => ({value: c.id, label: c.label}))}
                                         selected={draft.activeCategories.map(c => c.id)} excluding={excl('categories')}
                                         onChange={(ids, x) => change({
                                             activeCategories: categories.filter(c => ids.includes(c.id)),
                                             excluded: excluded('categories', x),
                                         })}/>,
        },
        {
            id: 'approval', label: fin('approval'), active: draft.approved !== '',
            status: approvals.find(a => a.value === draft.approved)?.label ?? '',
            render: close => <SitOptionList options={approvals} selected={[draft.approved]}
                                            onChange={([v]) => { change({approved: v as FinancialFilterValues['approved']}); close(); }}/>,
        },
    ];

    return (
        <SitFilterBar
            className="financial-filters-component"
            filters={filters} dirty={dirty}
            clearable={dirty || JSON.stringify(applied) !== JSON.stringify(noFilters)}
            onApply={() => fbStore.applyFilters(draft)}
            onClear={() => fbStore.applyFilters(noFilters)}/>
    );
});
